<!--赖传峰-->
<!--2021/12/28-->
<template>
  <ClientOnly>
    <teleport
      defer
      to="body"
    >
      <ul
        ref="contextMenu"
        :class="state.visible && 'show'"
        :style="contextMenuStyle"
        class="context-menu"
      >
        <li
          v-for="item in menuOptions"
          :key="item.operation"
          :aria-label="$t(item.labelI18nKey)"
          @click="commandHandler(item.operation)"
        >
          <a class="pp-link-button">
            <component :is="item.icon" />
            <span style="margin-left: 12px; text-align: left;">{{ $t(item.labelI18nKey) }}</span>
          </a>
        </li>
      </ul>
    </teleport>
  </ClientOnly>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
} from '#imports'
import {
  computePosition,
  computeTransformOrigin,
  operationsMeta,
  sizeStyle2Number,
} from '@/components/share/contextMenu.script'
import { onRightClickOutside } from '@/utils'
import {
  onClickOutside,
  useEventListener,
  useWindowSize,
} from '@vueuse/core'
import {
  type ContextMenuExpose,
  type ContextMenuState,
  Operation,
} from './contextMenu.type'

// eslint-disable-next-line no-spaced-func
const emit = defineEmits<{ (e: 'itemClick', operation: Operation, ids: string[]): void }>()

const contextMenu = ref<HTMLElement>()
const state = reactive<ContextMenuState>({
  visible: false,
  position: { left: 0, top: 0 },
  transformOrigin: 'top left',
  ids: [],
})

const contextMenuStyle = computed(() => ({
  left: `${state.position.left}px`,
  top: `${state.position.top}px`,
  'transform-origin': state.transformOrigin,
}))

const {
  width: innerWidth,
  height: innerHeight,
} = useWindowSize()

const menuOptions = computed(() => operationsMeta.filter(el => el.limit(null, state.ids)))

const close: ContextMenuExpose['close'] = () => Object.assign(state, {
  visible: false,
  position: { left: 0, top: 0 },
})

const show: ContextMenuExpose['show'] = async ({ x, y }, ids) => {
  Object.assign(state, {
    ids,
    visible: false,
    transformOrigin: { x, y },
  })

  await nextTick() // 确保菜单渲染完成，有高度

  if (!contextMenu.value) return
  const { width, height } = window.getComputedStyle(contextMenu.value)

  const left = computePosition(x, sizeStyle2Number(width), innerWidth.value)
  const top = computePosition(y, sizeStyle2Number(height), innerHeight.value)

  /* 保证 hide() 执行完成后， DOM 更新完成，再执行下面的代码，保证动画效果 */
  /* 勿使用 nextTick，不够完美 */
  setTimeout(() => Object.assign(state, {
    position: { left, top },
    transformOrigin: computeTransformOrigin({ x, y }, { x: left, y: top }),
    visible: true,
  }), 0)
}

// region 事件响应
const commandHandler = (operation: Operation) => {
  emit('itemClick', operation, state.ids)
  close()
}
useEventListener('contextmenu', (e) => {
  /* 不禁用 INPUT 与 TEXTAREA 的右键菜单，可能需要复制与粘贴功能 */
  if (['INPUT', 'TEXTAREA'].includes((e.target as Element).tagName)) return
  e.preventDefault()
})
// endregion

onMounted(() => {
  /**
   * FIXME @vueuse/core@8.4.0 的修复导致 onClickOutside 行为发生改变，菜单打开后即被关闭
   * onClickOutside: fallback to pointerup event if click event not propagate (#1522) (89c9e53)
   * https://github.com/vueuse/vueuse/pull/1522
   */
  onClickOutside(contextMenu, close) // 左键关闭
  onRightClickOutside(contextMenu, close) // 右键关闭
})

defineExpose<ContextMenuExpose>({
  close,
  show,
  dom: () => contextMenu.value,
})
</script>

<style
  lang="less"
  scoped
>
.context-menu {
  z-index: 2;
  background: #fff;
  position: fixed;
  min-width: 160px;
  box-shadow: var(--el-box-shadow-light);
  border: 1px solid var(--el-border-color-light);
  padding: 8px;
  border-radius: 8px;
  transform: scaleY(0); // 使用 scale 隐藏，可以保存宽高
  transition: unset;
  user-select: none; // 禁止文本选择

  &.show {
    transform: scaleY(1);
    transition: transform 0.1s ease-in-out;
  }
}

li {
  text-align: center;
  border-radius: 4px;
  padding: 0 10px;
  user-select: none; // 禁止文本选择

  a {
    height: 44px;
    justify-content: left;
    user-select: none; // 禁止文本选择
  }

  &:hover {
    background: var(--color-hover);
  }

  &.active {
    background: var(--color-active);
  }
}
</style>
